// ProductV2
(() => {
  if (customElements.get("product-v2")) return;

  class ProductV2 extends HTMLElement {
    constructor() {
      super();

      this.onProductChange = this.onProductChange.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener("product-new:change", this.onProductChange);
      this.addEventListener("change", this.onChange);
      this.addEventListener("click", this.onClick);

      window.queueMicrotask(() => {
        this.update(this.querySelector("product-new")?.selectedProducts);
      });
    }

    disconnectedCallback() {
      this.removeEventListener("product-new:change", this.onProductChange);
      this.removeEventListener("change", this.onChange);
      this.removeEventListener("click", this.onClick);
    }

    onClick(event) {
      const expandButton = event.target.closest("[data-bundle-expand]");
      if (!expandButton) return;

      const bundles = expandButton.closest("[data-bundle-collapsed]");
      if (!bundles) return;

      bundles.removeAttribute("data-bundle-collapsed");
      expandButton.setAttribute("aria-expanded", "true");
      // The link hides after expanding, so keep keyboard focus on the first revealed option
      bundles.querySelector("[data-bundle-extra] input")?.focus({ preventScroll: true });
    }

    onChange(event) {
      if (event.target.name === "quantity") this.syncAddedItems();
    }

    // Added mixers are cloned from the initial markup, so start them from the first mixer's current choice
    syncAddedItems() {
      const firstItem = this.querySelector('product-new-form-item[data-position="1"]');
      if (!firstItem) return;

      const checkedInputs = [...firstItem.querySelectorAll("input[type='radio']:checked")];

      this.querySelectorAll("product-new-form-item:not([data-position='1']):not([data-synced])").forEach((item) => {
        item.setAttribute("data-synced", "");

        checkedInputs.forEach((sourceInput) => {
          const input = [...item.querySelectorAll(`input[name="${sourceInput.name}"]`)].find(
            (candidate) => candidate.value === sourceInput.value
          );
          if (input && !input.checked) input.click();
        });
      });
    }

    onProductChange(event) {
      this.update(event.detail.selectedProducts);
    }

    update(selectedProducts) {
      const firstProduct = selectedProducts?.[0];
      if (!firstProduct) return;

      this.updateAvailability(selectedProducts);

      const selectedQuantity = parseInt(this.querySelector("input[name='quantity']:checked")?.value ?? 1, 10);

      const bundleOptions = [...this.querySelectorAll("[data-bundle-discount]")];
      const optionsCount = bundleOptions.length || 3;

      for (let quantity = 1; quantity <= optionsCount; quantity++) {
        const total = Array.from({ length: quantity }, (_, index) => selectedProducts[index]).reduce(
          (acc, product) => acc + parseFloat(product?.price ?? firstProduct.price),
          0
        );
        const optionDiscount = bundleOptions[quantity - 1]?.dataset.bundleDiscount;
        const discount = parseFloat(optionDiscount ?? firstProduct[`quantity_${quantity}`]?.discount ?? 0) || 0;
        const finalTotal = Math.round((total * (100 - discount)) / 100);
        const comparePrice = discount > 0 ? total : null;

        const bundlePriceEl = this.querySelector(`[data-bundle-price="${quantity}"]`);
        this.setPrice(
          bundlePriceEl?.querySelector("[data-bundle-final-price]"),
          bundlePriceEl?.querySelector("[data-bundle-compare-price]"),
          finalTotal,
          comparePrice
        );

        if (quantity !== selectedQuantity) continue;

        this.setPrice(
          this.querySelector("[data-product-v2-price]"),
          this.querySelector("[data-product-v2-compare-price]"),
          finalTotal,
          comparePrice
        );
        this.setPrice(this.querySelector("[data-product-new-total-price]"), null, finalTotal, null);
        this.setPrice(
          this.querySelector("[data-product-v2-total]"),
          this.querySelector("[data-product-v2-total-compare]"),
          finalTotal,
          comparePrice
        );
      }

      this.querySelectorAll("product-new-form-item").forEach((item) => {
        const thumbEl = item.querySelector("[data-product-new-form-thumb]");
        const itemProduct = selectedProducts[parseInt(item.dataset.position, 10) - 1];
        const thumbLayout = itemProduct?.size_image;

        const limitedBadge = item.querySelector("[data-limited-edition-badge]");
        if (limitedBadge && itemProduct) limitedBadge.hidden = itemProduct.limited_edition !== true;

        if (thumbEl && thumbLayout && thumbEl.innerHTML !== thumbLayout) {
          thumbEl.innerHTML = thumbLayout;
        }
      });
    }

    // Sold out mixers keep the shared product-new behaviour: the buy button becomes the
    // Klaviyo back-in-stock trigger, which Klaviyo replaces with its "Request a Restock" button
    updateAvailability(selectedProducts) {
      const soldOutProduct = selectedProducts.filter(Boolean).find((product) => !product.available);
      const buyButton = this.querySelector("form[data-product-new-buy-buttons] .product-new-buy-buttons__add-to-cart");

      this.classList.toggle("is-sold-out", Boolean(soldOutProduct));

      if (!soldOutProduct) return;

      // Point the Klaviyo form at the first unavailable mixer rather than the first one in the bundle
      buyButton?.setAttribute("data-product-variant", soldOutProduct.variant_id);

      // Klaviyo picks the restock state from the product in the URL, which product-new sets to the
      // last changed mixer right after this runs, so point it at the unavailable one instead
      if (this.querySelector("product-new")?.hasAttribute("data-is-pdp") && soldOutProduct.url) {
        setTimeout(() => {
          const url = new URL(soldOutProduct.url, window.location.origin);
          url.search = window.location.search;
          if (url.pathname !== window.location.pathname) window.history.replaceState({}, "", url);
        });
      }
    }

    setPrice(priceEl, comparePriceEl, priceInCents, comparePriceInCents) {
      if (priceEl) priceEl.textContent = this.formatPrice(priceInCents);

      if (!comparePriceEl) return;

      comparePriceEl.toggleAttribute("hidden", comparePriceInCents === null);
      if (comparePriceInCents !== null) {
        comparePriceEl.textContent = this.formatPrice(comparePriceInCents);
      }
    }

    formatPrice(priceInCents) {
      const price = priceInCents / 100;

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: this.dataset.currency || "USD",
        minimumFractionDigits: price % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(price);
    }
  }

  customElements.define("product-v2", ProductV2);
})();

// ProductV2Videos
(() => {
  if (customElements.get("product-v2-videos")) return;

  class ProductV2Videos extends HTMLElement {
    constructor() {
      super();

      this.initSlider = this.initSlider.bind(this);
      this.onClick = this.onClick.bind(this);
      this.pauseVideos = this.pauseVideos.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.onClick);

      if (window.Splide) {
        this.initSlider();
      } else {
        document.addEventListener("DOMContentLoaded", this.initSlider, { once: true });
      }
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);
      document.removeEventListener("DOMContentLoaded", this.initSlider);
      this.slider?.destroy();
      this.slider = null;
    }

    initSlider() {
      const sliderEl = this.querySelector("[data-videos-slider]");
      if (!sliderEl || !window.Splide || this.slider) return;

      this.slider = new Splide(sliderEl, {
        type: "loop",
        perPage: 1,
        perMove: 1,
        focus: "center",
        gap: "10px",
        padding: "30%",
        breakpoints: {
          989: { padding: "18px" },
        },
        arrows: false,
        pagination: true,
        updateOnMove: true,
      });
      this.slider.on("move", this.pauseVideos);
      this.slider.mount();
    }

    onClick(event) {
      const video = event.target.closest("[data-video-slide]")?.querySelector("video");
      if (!video) return;

      const isPlaying = video.hasAttribute("data-play");
      this.pauseVideos();
      if (isPlaying) return;

      video.setAttribute("data-play", "true");
      video.setAttribute("controls", "true");
      video.play()?.catch(() => {});
    }

    pauseVideos() {
      this.querySelectorAll("video[data-play]").forEach((video) => {
        video.pause();
        video.removeAttribute("data-play");
        video.removeAttribute("controls");
      });
    }
  }

  customElements.define("product-v2-videos", ProductV2Videos);
})();

// ProductV2Sticky
(() => {
  if (customElements.get("product-v2-sticky")) return;

  class ProductV2Sticky extends HTMLElement {
    constructor() {
      super();

      this.onClick = this.onClick.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.syncButtonState = this.syncButtonState.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector("[data-sticky-button]");
      this.buyForm = this.closest("product-v2")?.querySelector("form[data-product-new-buy-buttons]");
      this.buyButton = this.buyForm?.querySelector("button");
      if (!this.button || !this.buyButton) return;

      this.button.addEventListener("click", this.onClick);

      // The extended bottom margin keeps the form "intersecting" while it is in or below the viewport,
      // so the bar shows only once the buy buttons are scrolled past, even after an anchor jump.
      this.intersectionObserver = new IntersectionObserver(this.onIntersect, { rootMargin: "0px 0px 100000px 0px" });
      this.intersectionObserver.observe(this.buyForm);

      this.mutationObserver = new MutationObserver(this.syncButtonState);
      this.mutationObserver.observe(this.buyButton, { attributes: true, attributeFilter: ["disabled", "class"] });
      this.syncButtonState();
    }

    disconnectedCallback() {
      this.button?.removeEventListener("click", this.onClick);
      this.intersectionObserver?.disconnect();
      this.mutationObserver?.disconnect();
    }

    onClick() {
      if (!this.closest("product-v2")?.classList.contains("is-sold-out")) {
        this.buyButton.click();
        return;
      }

      // Klaviyo only opens its back-in-stock form on a real click, so bring its button into view instead
      const restockButton = this.buyForm.querySelector("[data-a11y-identifier^='bis-button']");
      this.buyForm.scrollIntoView({ behavior: "smooth", block: "center" });
      restockButton?.focus({ preventScroll: true });
    }

    onIntersect([entry]) {
      const isPastBuyButtons = !entry.isIntersecting;

      this.classList.toggle("is-visible", isPastBuyButtons);
      this.setAttribute("aria-hidden", String(!isPastBuyButtons));
      this.toggleAttribute("inert", !isPastBuyButtons);
    }

    syncButtonState() {
      this.button.toggleAttribute("disabled", this.buyButton.hasAttribute("disabled"));
      this.button.classList.toggle("is-loading", this.buyButton.classList.contains("loading"));
    }
  }

  customElements.define("product-v2-sticky", ProductV2Sticky);
})();
